import React, { useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, FormInstance, NumberInput } from 'rsuite';
import { AxiosError } from 'axios';
import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel } from '@/models/core.model';
import { PointAccountFundRequest } from '@/models/point-account.model';
import { fundPointAccount } from '@/services/point-account.service';
import { toastSuccessMessage } from '@/utils/toasterMessage';

const modelSchema = Schema.Model<PointAccountFundRequest>({
  amount: Schema.Types.NumberType().isRequired('This field is required.').min(1, 'Amount must be at least 1.'),
  note: Schema.Types.StringType(),
});

const emptyModel: PointAccountFundRequest = { amount: undefined, note: undefined };

const newIdempotencyKey = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

interface FundDrawerProps extends DrawerProps {
  pointAccountId: number;
  onSubmitted?: () => void;
}

const FundDrawer = (props: FundDrawerProps) => {
  const { open, onClose, onSubmitted, pointAccountId, ...rest } = props;
  const [formValue, setFormValue] = useState<PointAccountFundRequest>({ ...emptyModel });
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  // One key per drawer opening: a double-click / retry of the same submission is deduplicated by the API.
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey());
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  React.useEffect(() => {
    if (open) {
      setFormValue({ ...emptyModel });
      setIdempotencyKey(newIdempotencyKey());
    }
  }, [open]);

  const handleClose = (event?: React.SyntheticEvent) => {
    setErrorModel(undefined);
    setLoading(false);
    onClose?.(event as any);
  };

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) return;
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result = await fundPointAccount(pointAccountId, { amount: Number(formValue.amount), note: formValue.note || undefined, idempotencyKey });
      if (result.success) {
        toastSuccessMessage('Account funded');
        handleClose();
        onSubmitted?.();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right" open={open} onClose={handleClose} {...rest}>
      <Drawer.Header>
        <Drawer.Title>Fund Account</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">Submit</Button>
          <Button onClick={handleClose} appearance="subtle">Cancel</Button>
        </Drawer.Actions>
      </Drawer.Header>
      <Drawer.Body>
        <Form name="fund-point-account-form" fluid ref={formRef}
          onChange={value => setFormValue(pre => ({ ...pre, ...value }))}
          formValue={formValue} model={modelSchema} disabled={loading}
        >
          <Form.Group>
            <Form.Label>Amount</Form.Label>
            <Form.Control name="amount" accepter={NumberInput} min={1} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Note (optional)</Form.Label>
            <Form.Control name="note" />
          </Form.Group>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }} />}
      </Drawer.Body>
    </Drawer>
  );
};

export default FundDrawer;
